import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { FC, FormEvent, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { useMutation } from 'react-query';
import { BrandScreen } from '../../components/Brand';
import {
  ON_PRIMARY_FIELD_SX,
  ON_PRIMARY_LABEL_SX,
} from '../../components/fieldStyles';
import { colors } from '../../theme/tokens';

// "Nouveau mot de passe" (`/retrieve-password?email=…&timestamp=…`), the
// page the password-definition email leads to.
//
// Own design (no screen in the XD — DESIGN.md, gap 3): the Login brand
// screen (components/Brand/BrandScreen) with the form
// over the blue, white fields and white labels as in Login, and
// "Valider" as the primary orange button. Decisions:
//   - "Expired link" state in light orange (the colour of Login's
//     messages over blue), with a "Retour à la connexion" link: from
//     login the email is requested again with "Mot de passe oublié".
//   - Without `email` or without `timestamp` in the URL the link is useless, and
//     the same expired state is shown instead of a blank screen.
//   - While the link is being checked, a spinner: the check is a
//     server call and the form must not appear before.
//   - Save error inline, under the form, in light orange; the
//     former red Snackbar did not read over the blue.
//
// Behaviour kept: `POST /users/is-valid-timestamp` on open,
// `POST /users/set-password` with `email`, `password` and `timestamp`, the
// match validation, and on saving `ARCHIVOS_SET_PASSWORD` in
// sessionStorage and navigating to `/login`, where Login shows the notice. The ids
// `#password` / `#confirm_password` are the usual ones.

export const SESSION_STORAGE_SET_PASSWORD = 'ARCHIVOS_SET_PASSWORD';

const LABEL_SX = { ...ON_PRIMARY_LABEL_SX, mb: 0.75 } as const;

const EXPIRED_MESSAGE =
  'Votre lien de création de mot de passe a expiré. Veuillez refaire la démarche de création de mot de passe.';
const SAVE_ERROR_MESSAGE =
  "Une erreur s'est produite lors de la sauvegarde du mot de passe.";

// Plain fetch on purpose, like Login: this page is reached from an email, before any
// session exists, and archivosFetch would attach "Bearer null". Both routes are public.
const post = (path: string, data: Record<string, string>) =>
  fetch(`${process.env.REACT_APP_API_URL}/api/users/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

const RetrievePassword: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') ?? '';
  const timestamp = params.get('timestamp') ?? '';

  const [linkState, setLinkState] = useState<'checking' | 'valid' | 'expired'>(
    'checking',
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  const timestampMutation = useMutation({
    mutationFn: (data: Record<string, string>) =>
      post('is-valid-timestamp', data),
  });
  const { mutateAsync: setPassword, isLoading: isSaving } = useMutation({
    mutationFn: (data: Record<string, string>) => post('set-password', data),
  });

  useEffect(() => {
    document.title = 'ArchivOs - Nouveau mot de passe';
  }, []);

  useEffect(() => {
    if (!email || !timestamp) {
      setLinkState('expired');
      return;
    }

    let cancelled = false;
    setLinkState('checking');

    timestampMutation
      .mutateAsync({ timestamp })
      .then((response) => {
        if (!cancelled) setLinkState(response.status === 200 ? 'valid' : 'expired');
      })
      .catch(() => {
        if (!cancelled) setLinkState('expired');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, timestamp]);

  const validate = (password: string, confirmed: string): string => {
    if (!password || !confirmed) return 'Les 2 champs sont obligatoires.';
    if (password !== confirmed)
      return 'Les mots de passe doivent être identiques.';
    return '';
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;

    const data = new FormData(event.currentTarget);
    const password = data.get('password') as string;
    const confirmed = data.get('confirm_password') as string;

    const found = validate(password, confirmed);
    setErrorMessage(found);
    setSaveError('');
    if (found) return;

    setPassword({
      email,
      password,
      // The server verifies this token before changing the password: it proves the
      // caller opened the emailed link for this address.
      timestamp,
    })
      .then((response) => {
        if (response.status === 200) {
          sessionStorage.setItem(SESSION_STORAGE_SET_PASSWORD, 'true');
          navigate('/login');
        } else {
          setSaveError(SAVE_ERROR_MESSAGE);
        }
      })
      .catch(() => setSaveError(SAVE_ERROR_MESSAGE));
  };

  return (
    <BrandScreen>
      <Typography
        component="h2"
        variant="h2"
        sx={{ mt: 5, color: colors.white }}
      >
        Nouveau mot de passe
      </Typography>

      {linkState === 'checking' && (
        <Box
          data-testid="retrieve-checking"
          sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}

      {linkState === 'expired' && (
        <Box
          data-testid="retrieve-expired"
          sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Typography role="alert" sx={{ color: colors.accentLight }}>
            {EXPIRED_MESSAGE}
          </Typography>
          <Link
            component={RouterLink}
            to="/login"
            variant="actionLink"
            underline="hover"
            sx={{ color: colors.accentLight, alignSelf: 'flex-start' }}
          >
            Retour à la connexion
          </Link>
        </Box>
      )}

      {linkState === 'valid' && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2.5 }}
        >
          <FormControl fullWidth error={!!errorMessage}>
            <FormLabel htmlFor="password" sx={LABEL_SX}>
              Nouveau mot de passe
            </FormLabel>
            <TextField
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              autoFocus
              required
              fullWidth
              error={!!errorMessage}
              onChange={() => setErrorMessage('')}
              variant="outlined"
              sx={ON_PRIMARY_FIELD_SX}
            />
          </FormControl>

          <FormControl fullWidth error={!!errorMessage}>
            <FormLabel htmlFor="confirm_password" sx={LABEL_SX}>
              Confirmez le mot de passe
            </FormLabel>
            <TextField
              id="confirm_password"
              name="confirm_password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              error={!!errorMessage}
              onChange={() => setErrorMessage('')}
              variant="outlined"
              sx={ON_PRIMARY_FIELD_SX}
            />
          </FormControl>

          {(errorMessage || saveError) && (
            <Typography role="alert" sx={{ color: colors.accentLight }}>
              {errorMessage || saveError}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            size="large"
            disabled={isSaving}
            sx={{ mt: 1 }}
          >
            Valider
          </Button>
        </Box>
      )}
    </BrandScreen>
  );
};

export default RetrievePassword;
